import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Chip,
  Avatar,
  Stack,
  Divider,
} from "@mui/material";
import {
  Assignment as TicketIcon,
  Download as DownloadIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  HourglassTop as PendingIcon,
  AssignmentInd as AssignedIcon,
  CheckCircle as RespondedIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import LeftToolbar from "./LeftToolbar";

import {
  PieChart,
  BarChart,
  Pie,
  Bar,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";
const BRAND = "#115095";
const ACCENT = "#0FB5AE"; 
const COLORS = [
  "#115095",
  "#FF6B35",
  "#4CAF50",
  "#FFC107",
  "#9C27B0",
  "#FF5722",
  "#00BCD4",
  "#8BC34A",
  "#E91E63",
  "#607D8B",
  "#3F51B5",
  "#CDDC39",
  "#795548",
  "#F44336",
  "#009688",
  "#673AB7",
  "#AED581",
  "#FF9800",
  "#BDBDBD",
  "#00ACC1",
];
const RADIUS = "14px";
const SHADOW = "0 2px 10px rgba(17,80,149,0.08)";
const SHADOW_HOVER = "0 8px 20px rgba(17,80,149,0.14)";

const EMPTY_DASHBOARD = {
  summary: {
    CreatedTicketsCount: 0,
    PendingCount: 0,
    AssignedCount: 0,
    RespondedCount: 0,
  },
  categoryDistribution: [],
  sectorDistribution: [],
  institutionDistribution: [],
  agentRankings: [],
  assigneeRankings: [],
  respondedDetails: [],
};
const ALL_TIME = 0;
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Card
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 1.5,
          boxShadow: SHADOW_HOVER,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {data.Name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Count: {data.Value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Percentage: {data.Percentage}%
        </Typography>
      </Card>
    );
  }
  return null;
};

const SummaryList = ({ data, total, loading }) =>
  loading ? (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
      <CircularProgress size={24} />
    </Box>
  ) : (
    <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
      {data.map((item, index) => (
        <Box
          key={item.Name}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.4,
            borderBottom: "1px solid",
            borderColor: "divider",
            "&:last-child": { borderBottom: "none" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: COLORS[index % COLORS.length],
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                maxWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.Name}
            >
              {item.Name}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {item.Value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.Percentage}%
            </Typography>
          </Box>
        </Box>
      ))}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.4,
          mt: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "rgba(17,80,149,0.04)",
          borderRadius: 1,
          px: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Total
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {data.reduce((sum, item) => sum + item.Value, 0)} / {total}
        </Typography>
      </Box>
    </Box>
  );

// A chart + summary-list pair
const DistributionSection = ({ title, data, total, loading, type = "pie", isMobile }) => (
  <Grid container spacing={3} sx={{ mt: 1 }}>
    <Grid item xs={12} md={8}>
      <Card sx={{ height: "100%", boxShadow: SHADOW, borderRadius: RADIUS }}>
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ mb: 2, color: BRAND, fontWeight: 700 }}>
            {title}
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
              <CircularProgress />
            </Box>
          ) : data.length > 0 ? (
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                {type === "pie" ? (
                  <PieChart>
                    <Pie
                      data={data.slice(0, 10)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ Percentage, cx, cy, midAngle, innerRadius, outerRadius }) => {
                        if (Percentage < 1) return "";
                        const RADIAN = Math.PI / 180;
                        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text
                            x={x}
                            y={y}
                            fill="white"
                            textAnchor={x > cx ? "start" : "end"}
                            dominantBaseline="central"
                            fontSize={12}
                            fontWeight="bold"
                          >
                            {`${Math.round(Percentage)}%`}
                          </text>
                        );
                      }}
                      outerRadius={isMobile ? 100 : 150}
                      dataKey="Value"
                      nameKey="Name"
                    >
                      {data.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      layout={isMobile ? "horizontal" : "vertical"}
                      verticalAlign={isMobile ? "bottom" : "middle"}
                      align={isMobile ? "center" : "left"}
                      wrapperStyle={{ paddingTop: isMobile ? 20 : 0 }}
                    />
                  </PieChart>
                ) : (
                  <BarChart data={data.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="Name" type="category" tick={false} axisLine={true} />
                    <YAxis type="number" label={{ value: "Tickets", angle: -90, position: "insideLeft" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Value" name="Tickets" radius={[6, 6, 0, 0]}>
                      {data.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 8 }}>
              No data available for the selected period
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={4}>
      <Card sx={{ height: "100%", boxShadow: SHADOW, borderRadius: RADIUS }}>
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ mb: 2, color: BRAND, fontWeight: 700 }}>
            {title.replace("Tickets by", "")} Summary
          </Typography>
          <SummaryList data={data} total={total} loading={loading} />
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// A ranked leaderboard card
const RankingCard = ({ title, data, loading }) => (
  <Card sx={{ height: "100%", boxShadow: SHADOW, borderRadius: RADIUS }}>
    <CardContent>
      <Typography variant="h6" component="h2" sx={{ mb: 2, color: BRAND, fontWeight: 700 }}>
        {title}
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : data.length > 0 ? (
        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {data.map((item, index) => (
            <Box
              key={`${item.Name}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.4,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 13,
                    color: item.RankPosition <= 3 ? "#fff" : "text.primary",
                    backgroundColor:
                      item.RankPosition === 1
                        ? "#FFC107"
                        : item.RankPosition === 2
                          ? "#BDBDBD"
                          : item.RankPosition === 3
                            ? "#CD7F32"
                            : "rgba(0,0,0,0.05)",
                    flexShrink: 0,
                  }}
                >
                  {item.RankPosition}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {item.Name}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {item.TicketsHandled}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 8 }}>
          No data available for the selected period
        </Typography>
      )}
    </CardContent>
  </Card>
);

const MetricCard = ({ label, value, loading, color, Icon, onClick }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        backgroundColor: "#FAFBFC",
        borderLeft: `4px solid ${color}`,
        borderRadius: RADIUS,
        boxShadow: SHADOW,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": onClick
          ? { boxShadow: SHADOW_HOVER, transform: "translateY(-2px)" }
          : undefined,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              backgroundColor: `${color}1A`,
              borderRadius: "50%",
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 26, color }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 700, mt: 0.5 }}>
              {loading ? <CircularProgress size={22} sx={{ color }} /> : value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);
const transformDashboardData = (apiData) => {
  const dashboardSummary = apiData.dashboard?.[0] || {};
  const totalCreated = dashboardSummary.TotalCreatedTickets || 0;

  const withPercentages = (items) =>
    items.map((item) => ({
      ...item,
      Percentage: totalCreated > 0 ? Math.round((item.Value / totalCreated) * 100) : 0,
    }));

  const sectorDistribution = withPercentages(
    (apiData.sectors || [])
      .filter((s) => s.CreatedTickets > 0)
      .map((sector) => ({ Name: sector.SectorName, Value: sector.CreatedTickets || 0 })),
  );

  const categoryDistribution = withPercentages(
    (apiData.categories || [])
      .filter((c) => c.CreatedTickets > 0)
      .map((category) => ({ Name: category.CategoryName, Value: category.CreatedTickets || 0 })),
  );

  const institutionDistribution = withPercentages(
    (apiData.institutions || [])
      .filter((i) => i.CreatedTickets > 0)
      .map((institution) => ({ Name: institution.InstitutionName, Value: institution.CreatedTickets || 0 })),
  );
  const agentRankings = (apiData.supportAgents || [])
    .slice()
    .sort((a, b) => (b.RespondedTickets || 0) - (a.RespondedTickets || 0))
    .map((agent, index) => ({
      Name: agent.Agent,
      TicketsHandled: agent.RespondedTickets || 0,
      RankPosition: index + 1,
    }));

  // Leaderboard of who tickets were assigned to.
  const assigneeRankings = (apiData.assignedPerson || [])
    .slice()
    .sort((a, b) => (b.RespondedTickets || 0) - (a.RespondedTickets || 0))
    .map((person, index) => ({
      Name: person.Assigned,
      TicketsHandled: person.RespondedTickets || 0,
      RankPosition: index + 1,
    }));

  const summary = {
    CreatedTicketsCount: totalCreated,
    PendingCount: (dashboardSummary.NewPendingTickets || 0) + (dashboardSummary.OldPendingTickets || 0),
    AssignedCount: dashboardSummary.AssignedTickets || 0,
    RespondedCount: dashboardSummary.QuarterClosedTickets || 0,
  };

  const respondedDetails = (apiData.respondedTickets || []).map((t) => ({
    TicketId: t.TicketId || "N/A",
    Sender: t.Sender || "N/A",
    InstitutionName: t.InstitutionName || "Unknown",
    SectorName: t.SectorName || "N/A",
    categoryName: t.categoryName || "Unknown",
    UserRoleId: t.UserRoleId || "N/A",
    RespondedBy: t.RespondedBy || "N/A",
    CreatedOn: t.CreatedOn || null,
    ClosedOn: t.ClosedOn || null,
    ticketDescription: t.ticketDescription || "",
    Response: t.Response || "N/A",
  }));

  return {
    summary,
    categoryDistribution,
    sectorDistribution,
    institutionDistribution,
    agentRankings,
    assigneeRankings,
    respondedDetails,
  };
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [fiscalYearId, setFiscalYearId] = useState(null);
  const [quarterId, setQuarterId] = useState("");
  const [fiscalYearQuarters, setFiscalYearQuarters] = useState({});
  const [user] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch {
      return {};
    }
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const resetAllFilters = () => {
    const fiscalYears = Object.keys(fiscalYearQuarters);
    if (fiscalYears.length > 0) {
      const latestFiscalYear = fiscalYears[fiscalYears.length - 1];
      setFiscalYearId(Number(latestFiscalYear));
      const latestQuarter = fiscalYearQuarters[latestFiscalYear][0];
      setQuarterId(latestQuarter ? latestQuarter.id : "");
    } else {
      setFiscalYearId(ALL_TIME);
      setQuarterId("");
    }
  };
  const currentQuarters = fiscalYearId && fiscalYearId !== ALL_TIME ? fiscalYearQuarters[fiscalYearId] || [] : [];

  const periodLabel = () => {
    if (fiscalYearId === ALL_TIME) return "All Time";
    if (!fiscalYearId) return "";
    if (!quarterId) return `FY ${fiscalYearId}`;
    const q = currentQuarters.find((qq) => String(qq.id) === String(quarterId));
    return `FY ${fiscalYearId} - ${q ? q.name : ""}`;
  };
  const fetchFiscalYearsQuarters = async () => {
    try {
      const res = await axios.get("/api/support/fiscalYears-quarters");
      setFiscalYearQuarters(res.data || {});
      const fiscalYears = Object.keys(res.data || {});
      if (fiscalYears.length > 0) {
        const latestFiscalYear = fiscalYears[fiscalYears.length - 1];
        setFiscalYearId(Number(latestFiscalYear));
        const latestQuarter = res.data[latestFiscalYear][0];
        setQuarterId(latestQuarter ? latestQuarter.id : "");
      } else {
        setFiscalYearId(ALL_TIME);
      }
    } catch (err) {
      console.error("Failed loading fiscal years", err);
      setFiscalYearId(ALL_TIME);
    }
  };

  // Only fiscal year + quarter drive what data is shown.
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Wipe the previous period's data right away so charts/rankings/summary
      // don't sit there showing stale numbers (or animate weirdly) while the
      // new request is in flight — they'll show their loading state instead.
      setDashboardData(EMPTY_DASHBOARD);

      // When "All Time" is selected, omit both params entirely so the
      // backend receives fiscalYearId/quarterId as null and returns
      // everything, instead of literally filtering on fiscalYearId = 0.
      const isAllTime = fiscalYearId === ALL_TIME;
      const params = {
        fiscalYearId: isAllTime ? undefined : fiscalYearId ?? undefined,
        quarterId: isAllTime ? undefined : quarterId || undefined,
      };

      const res = await axios.get("/api/support/dashboardData", { params });
      const transformedData = transformDashboardData(res.data);

      setDashboardData({
        ...EMPTY_DASHBOARD,
        ...transformedData,
      });
    } catch (err) {
      console.error("Dashboard loading error:", err);
      setError(err.response?.data?.message || "Failed loading dashboard data");
    } finally {
      setLoading(false);
    }
  }, [fiscalYearId, quarterId]);

  useEffect(() => {
    fetchFiscalYearsQuarters();
  }, []);

  useEffect(() => {
    if (fiscalYearId !== null) {
      fetchDashboardData();
    }
  }, [fiscalYearId, quarterId, fetchDashboardData]);
  const exportToExcel = () => {
    const { summary, categoryDistribution, sectorDistribution, institutionDistribution, respondedDetails } = dashboardData;
    if (!categoryDistribution.length && !sectorDistribution.length && !respondedDetails.length) {
      setError("No data found for the selected period");
      return;
    }
    const toRows = (items) => items.map((i) => ({ Name: i.Name, Count: i.Value, Percentage: `${i.Percentage}%` }));
    const overviewRows = [
      { Field: "Report", Value: "Support Dashboard Report" },
      { Field: "Period", Value: periodLabel() || "All Time" },
      { Field: "Created Tickets", Value: summary.CreatedTicketsCount },
      { Field: "Pending Tickets", Value: summary.PendingCount },
      { Field: "Assigned Tickets", Value: summary.AssignedCount },
      { Field: "Responded Tickets", Value: summary.RespondedCount },
      { Field: "Generated On", Value: new Date().toLocaleString() },
    ];

    const ticketRows = respondedDetails.map((t) => ({
      "Ticket ID": t.TicketId,
      Sender: t.Sender,
      Institution: t.InstitutionName,
      Sector: t.SectorName,
      Category: t.categoryName,
      Role: t.UserRoleId,
      "Responded By": t.RespondedBy,
      "Created On": t.CreatedOn ? new Date(t.CreatedOn).toLocaleString() : "N/A",
      "Closed On": t.ClosedOn ? new Date(t.ClosedOn).toLocaleString() : "N/A",
      Description: t.ticketDescription,
      Response: t.Response,
    }));

    const wb = XLSX.utils.book_new();
    const overviewSheet = XLSX.utils.json_to_sheet(overviewRows, { skipHeader: true });
    overviewSheet["!cols"] = [{ wch: 20 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, overviewSheet, "Overview");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toRows(categoryDistribution)), "By Category");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toRows(sectorDistribution)), "By Sector");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toRows(institutionDistribution)), "By Institution");

    if (ticketRows.length > 0) {
      const ticketSheet = XLSX.utils.json_to_sheet(ticketRows);
      ticketSheet["!cols"] = [
        { wch: 16 }, // Ticket ID
        { wch: 22 }, // Sender
        { wch: 28 }, // Institution
        { wch: 16 }, // Sector
        { wch: 16 }, // Category
        { wch: 12 }, // Role
        { wch: 20 }, // Responded By
        { wch: 20 }, // Created On
        { wch: 20 }, // Closed On
        { wch: 50 }, // Description
        { wch: 50 }, // Response
      ];
      XLSX.utils.book_append_sheet(wb, ticketSheet, "Responded Tickets");
    }

    const period = periodLabel().replace(/ /g, "_");
    XLSX.writeFile(wb, `Dashboard_Report_${period}_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    try {
      const headerImage = require("./assets/headerImage.png");
      doc.addImage(headerImage, "PNG", 14, 10, 182, 50);
    } catch (err) {
      console.error("Error loading header image:", err);
    }

    const { summary, categoryDistribution, sectorDistribution, institutionDistribution, respondedDetails } = dashboardData;

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 80, 149);
    doc.text("Support Dashboard Report", 14, 66);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(90, 90, 90);
    doc.text(`Period: ${periodLabel() || "All Time"}`, 14, 72);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Summary Statistics", 14, 82);
    autoTable(doc, {
      startY: 87,
      head: [["Tickets", "Count"]],
      body: [
        ["Created Tickets", summary.CreatedTicketsCount],
        ["Pending Tickets", summary.PendingCount],
        ["Assigned Tickets", summary.AssignedCount],
        ["Responded Tickets", summary.RespondedCount],
      ],
      theme: "grid",
      headStyles: { fillColor: [17, 80, 149], textColor: [255, 255, 255], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14 },
    });

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Category Distribution", 14, doc.lastAutoTable.finalY + 16);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 21,
      head: [["Category", "Count", "Percentage"]],
      body: categoryDistribution.map((item) => [item.Name, item.Value, `${item.Percentage}%`]),
      theme: "grid",
      headStyles: { fillColor: [17, 80, 149], textColor: [255, 255, 255], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14 },
    });

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Sector Distribution", 14, doc.lastAutoTable.finalY + 16);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 21,
      head: [["Sector", "Count", "Percentage"]],
      body: sectorDistribution.map((item) => [item.Name, item.Value, `${item.Percentage}%`]),
      theme: "grid",
      headStyles: { fillColor: [17, 80, 149], textColor: [255, 255, 255], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14 },
    });

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Institution Distribution", 14, doc.lastAutoTable.finalY + 16);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 21,
      head: [["Institution", "Count", "Percentage"]],
      body: institutionDistribution.map((item) => [item.Name, item.Value, `${item.Percentage}%`]),
      theme: "grid",
      headStyles: { fillColor: [17, 80, 149], textColor: [255, 255, 255], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14 },
    });

    if (respondedDetails.length > 0) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const detailsTitleY = doc.lastAutoTable.finalY + 16;
      doc.text("Responded Tickets", 14, detailsTitleY);
      autoTable(doc, {
        startY: detailsTitleY + 5,
        head: [["Ticket ID", "Sender", "Institution", "Category", "Responded By", "Description", "Response"]],
        body: respondedDetails.map((t) => [
          t.TicketId,
          t.Sender,
          t.InstitutionName,
          t.categoryName,
          t.RespondedBy,
          t.ticketDescription,
          t.Response,
        ]),
        theme: "grid",
        headStyles: { fillColor: [17, 80, 149], textColor: [255, 255, 255], fontSize: 7, fontStyle: "bold" },
        bodyStyles: { fontSize: 6, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 22 },
          2: { cellWidth: 26 },
          3: { cellWidth: 18 },
          4: { cellWidth: 22 },
          5: { cellWidth: 40 },
          6: { cellWidth: 34 },
        },
        pageBreak: "auto",
        margin: { left: 14 },
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, 14, 285);
      if (i === pageCount) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const footerText = `This Report was generated from IPPIS System on ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} ${new Date().toLocaleTimeString("en-GB")} by ${user.firstName || ""} ${user.lastName || ""}`;
        doc.text(footerText, 105, 275, { align: "center" });
      }
    }

    const period = periodLabel();
    doc.save(`Dashboard_Report_${period.replace(/ /g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const {
    summary,
    categoryDistribution,
    sectorDistribution,
    institutionDistribution,
    agentRankings,
    assigneeRankings,
  } = dashboardData;

  const hasFilters = (fiscalYearId !== null && fiscalYearId !== ALL_TIME) || quarterId;

  return (
    <Box sx={{ display: "flex", backgroundColor: "#F6F8FA", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${collapsed ? 80 : 280}px)` },
          ml: { sm: `${collapsed ? 80 : 280}px` },
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(10px)",
          color: "text.primary",
          boxShadow: "0 1px 0 rgba(17,80,149,0.06), 0 10px 24px rgba(17,80,149,0.06)",
          borderBottom: "1px solid rgba(17,80,149,0.06)",
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -1,
            height: 3,
            background: `linear-gradient(90deg, ${BRAND}, ${ACCENT})`,
          },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, px: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 800,
                lineHeight: 1.25,
                letterSpacing: "-0.01em",
                background: `linear-gradient(90deg, ${BRAND}, ${ACCENT})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                width: "fit-content",
              }}
            >
              Dashboard
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: { xs: "none", sm: "block" }, color: "#6B7A8F" }}
              noWrap
            >
              {new Date().toLocaleDateString("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </Typography>
          </Box>

          <Tooltip title="Pending tickets">
            <IconButton
              onClick={() => navigate("/PendingTickets")}
              sx={{
                backgroundColor:
                  summary.PendingCount > 0 ? "rgba(255,193,7,0.14)" : "rgba(17,80,149,0.06)",
                borderRadius: "12px",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    summary.PendingCount > 0 ? "rgba(255,193,7,0.24)" : "rgba(17,80,149,0.12)",
                },
              }}
            >
              <Badge
                badgeContent={summary.PendingCount > 999 ? "999+" : summary.PendingCount}
                color="error"
                max={9999}
              >
                <NotificationsIcon
                  sx={{ color: summary.PendingCount > 0 ? "#B8860B" : BRAND, fontSize: 22 }}
                />
              </Badge>
            </IconButton>
          </Tooltip>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", sm: "block" }, my: 1.5, borderColor: "rgba(17,80,149,0.1)" }}
          />

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ display: { xs: "none", sm: "flex" } }}>
            <Avatar
              sx={{
                width: 38,
                height: 38,
                fontSize: 14,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${BRAND}, ${ACCENT})`,
                boxShadow: "0 3px 10px rgba(17,80,149,0.25)",
              }}
            >
              {`${(user.firstName || "U").charAt(0)}${(user.lastName || "").charAt(0)}`.toUpperCase()}
            </Avatar>
            <Box sx={{ lineHeight: 1.25, maxWidth: 140 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {user.firstName || "User"} {user.lastName || ""}
              </Typography>
              <Typography variant="caption" noWrap sx={{ display: "block", color: "#6B7A8F" }}>
                {user.role || "Support Team"}
              </Typography>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isMobile ? 2 : 4,
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          marginLeft: { sm: collapsed ? "80px" : "280px" },
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: { width: "100%", marginLeft: 0, pt: 8 },
          pt: 12,
        }}
      >
        {error && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#FDECEA",
              border: "1px solid #F5C6C0",
            }}
          >
            <Typography color="error" sx={{ fontWeight: 500 }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            mb: 3,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: BRAND }}>
              Dashboard and Reports
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {periodLabel() || "Loading period…"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={{ backgroundColor: BRAND, "&:hover": { backgroundColor: "#0d3c6e" }, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToPDF}
              sx={{ color: BRAND, borderColor: BRAND, "&:hover": { borderColor: "#0d3c6e", backgroundColor: "rgba(17,80,149,0.04)" }, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
            >
              PDF
            </Button>
          </Box>
        </Box>

        {/* Fiscal year / quarter filter */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 4,
            p: 2,
            backgroundColor: "#fff",
            borderRadius: RADIUS,
            boxShadow: SHADOW,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Fiscal Year</InputLabel>
            <Select
              value={fiscalYearId ?? ""}
              label="Fiscal Year"
              onChange={(e) => {
                const selectedFY = Number(e.target.value);
                setFiscalYearId(selectedFY);
                const quartersForYear = fiscalYearQuarters[selectedFY];
                if (selectedFY !== ALL_TIME && quartersForYear?.length > 0) {
                  setQuarterId(quartersForYear[0].id);
                } else {
                  setQuarterId("");
                }
              }}
              sx={{ backgroundColor: "#fff", borderRadius: "10px" }}
            >
              <MenuItem value={ALL_TIME}>All Time</MenuItem>
              {Object.keys(fiscalYearQuarters).map((fy) => (
                <MenuItem key={fy} value={Number(fy)}>
                  {fy}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }} disabled={!fiscalYearId || fiscalYearId === ALL_TIME}>
            <InputLabel>Quarter</InputLabel>
            <Select
              value={quarterId || ""}
              label="Quarter"
              onChange={(e) => setQuarterId(e.target.value === "" ? "" : Number(e.target.value))}
              sx={{ backgroundColor: "#fff", borderRadius: "10px" }}
            >
              <MenuItem value="">Whole Year</MenuItem>
              {currentQuarters.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasFilters && (
            <>
              <Chip
                label={fiscalYearId === ALL_TIME ? "All Time" : `FY ${fiscalYearId}`}
                sx={{ borderRadius: "10px", fontWeight: 500 }}
              />
              {quarterId && fiscalYearId !== ALL_TIME && (
                <Chip
                  label={currentQuarters.find((q) => String(q.id) === String(quarterId))?.name || "Quarter"}
                  onDelete={() => setQuarterId("")}
                  sx={{ borderRadius: "10px", fontWeight: 500 }}
                />
              )}
              <Button variant="text" onClick={resetAllFilters} startIcon={<ClearIcon />} sx={{ borderRadius: "10px", color: "text.secondary", textTransform: "none" }}>
                Reset
              </Button>
            </>
          )}
        </Box>

        {/* Metrics */}
        <Grid container spacing={3}>
          <MetricCard
            label="Pending Tickets"
            value={summary.PendingCount}
            loading={loading}
            color="#FFC107"
            Icon={PendingIcon}
            onClick={() => navigate("/PendingTickets")}
          />
          <MetricCard
            label="Assigned Tickets"
            value={summary.AssignedCount}
            loading={loading}
            color="#17A2B8"
            Icon={AssignedIcon}
            onClick={() => navigate("/AssignedTickets")}
          />
          <MetricCard
            label="Responded Tickets"
            value={summary.RespondedCount}
            loading={loading}
            color="#28A745"
            Icon={RespondedIcon}
            onClick={() => navigate("/RespondedTickets")}
          />
          <MetricCard label="Created Tickets" value={summary.CreatedTicketsCount} loading={loading} color={BRAND} Icon={TicketIcon} />
        </Grid>

        {/* Distributions */}
        <DistributionSection
          title="Tickets by Category"
          data={categoryDistribution}
          total={summary.CreatedTicketsCount}
          loading={loading}
          type="pie"
          isMobile={isMobile}
        />
        <DistributionSection
          title="Tickets by Sectors"
          data={sectorDistribution}
          total={summary.CreatedTicketsCount}
          loading={loading}
          type="pie"
          isMobile={isMobile}
        />
        <DistributionSection
          title="Tickets by Institutions"
          data={institutionDistribution}
          total={summary.CreatedTicketsCount}
          loading={loading}
          type="bar"
          isMobile={isMobile}
        />

        {/* Rankings */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <RankingCard title="Top Responders" data={agentRankings} loading={loading} />
          </Grid>
          <Grid item xs={12} md={6}>
            <RankingCard title="Top Assignees (Most Assigned Tickets)" data={assigneeRankings} loading={loading} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;